import { useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { FormRadioGroup, LwFormAutocomplete, LwFormInput, LwFormMultiSelect } from 'redesign';
import { useMemo, useState } from 'react';
import { FlexWorkerForNotWelcomeList } from 'ogp/services/company-service.types';
import { RenderUnsettledUI } from 'shared/components';
import { GetDepartmentsResponse } from 'ogp/services/department-service.types';
import { InArray } from 'shared/utils/ts-utils';
import { useDebounce } from 'shared/hooks/use-debounce';
import { EmploymentType } from '@types';
import { useGetAvailableFlexWorkers } from './hooks/use-get-available-flex-workers';
import { useGetDepartmentsAsList } from '../../../departments/hooks';

export type NotWelcomeListFormCancellationMode = 'cancelAll' | 'cancelWithoutRule';

export type AddWorkerToNotWelcomeListForm = {
  flexWorker: FlexWorkerForNotWelcomeList;
  reason: string;
  departments: GetDepartmentsResponse;
  shouldCancelAll: boolean;
  shouldCancelWithoutRule: boolean;
  cancellationMode: NotWelcomeListFormCancellationMode;
};

const NotWelcomeListAddFlexWorker = () => {
  const { control, setValue, watch } = useFormContext<AddWorkerToNotWelcomeListForm>();

  const selectedFlexWorker = watch('flexWorker');

  const [input, setSelected] = useState<string>('');
  const debouncedInput = useDebounce(input, 300);
  const getAvailableFlexworkersQuery = useGetAvailableFlexWorkers(debouncedInput);
  const getDepartmentsQuery = useGetDepartmentsAsList({ includeArchived: false });

  const departmentOptions = useMemo(() => {
    return getDepartmentsQuery.data
      ? [
          { id: 'all', name: 'all', branchOffice: { name: '', id: '' } },
          ...getDepartmentsQuery.data,
        ]
      : [];
  }, [getDepartmentsQuery.data]);

  const getOptionLabel = (option: InArray<GetDepartmentsResponse>) =>
    option.name === 'all' ? 'Alle afdelingen' : option.branchOffice.name + ': ' + option.name;

  /**
   * If the user selects 'all' departments, we should only store that in the form state.
   * We don't allow the user to select individual departments if 'all' is selected.
   * Once 'all' is unselected, the user can select individual departments.
   */
  const onSelectDepartment = (_event: any, value: GetDepartmentsResponse) => {
    if (value.find((v) => v.id === 'all')) {
      setValue('departments', [{ id: 'all', name: 'all', branchOffice: { name: '', id: '' } }]);
    } else {
      setValue('departments', value);
    }
  };

  if (getAvailableFlexworkersQuery.status === 'error') {
    return <RenderUnsettledUI data={getAvailableFlexworkersQuery} />;
  }

  if (getDepartmentsQuery.status === 'error') {
    return <RenderUnsettledUI data={getDepartmentsQuery} />;
  }

  const getWorkerOptionLabel = (option: FlexWorkerForNotWelcomeList | Record<string, never>) => {
    if (!option || Object.keys(option).length === 0) {
      return '';
    }

    const fullName = [option.firstName, option.infix, option.lastName].filter(Boolean).join(' ');
    const fullNameWithEmail = `${fullName} (${option.email})`;
    return `${fullNameWithEmail}`;
  };

  const cancelAllOption: { label: string; value: NotWelcomeListFormCancellationMode } = {
    label: 'Alles annuleren',
    value: 'cancelAll',
  };
  const cancelWithoutRuleOption: { label: string; value: NotWelcomeListFormCancellationMode } = {
    label: 'Alleen shiftclaims annuleren waar de 50%-regel niet van toepassing is',
    value: 'cancelWithoutRule',
  };

  return (
    <>
      <Grid style={{ alignItems: 'end' }} container item spacing={2} xs={12}>
        <Grid item xs={12}>
          <LwFormAutocomplete
            name="flexWorker"
            label="Flexwerker"
            getOptionLabel={getWorkerOptionLabel}
            getOptionKey={(option) => option.id ?? ''}
            isOptionEqualToValue={(option, value) => {
              return option?.email === value?.email;
            }}
            disableClearable={true}
            options={getAvailableFlexworkersQuery.data ?? []}
            isLoading={getAvailableFlexworkersQuery.isFetching}
            control={control}
            rules={{ required: true }}
            onChange={(_event, value) => {
              if (value) {
                setValue('flexWorker', value);
                if (
                  value.employmentType === EmploymentType.EMPLOYEE ||
                  value.employmentType === EmploymentType.TEMPWORKER
                ) {
                  setValue('shouldCancelAll', true);
                  setValue('shouldCancelWithoutRule', false);
                  setValue('cancellationMode', 'cancelAll');
                }
              }
            }}
            onInputChange={(_e, value) => {
              setSelected(value);
            }}
            data-testid="fw-select"
          />
        </Grid>
        <Grid item xs={12}>
          <LwFormInput control={control} label="Reden" name="reason" />
        </Grid>
        <Grid item xs={12}>
          <LwFormMultiSelect
            control={control}
            label="Afdelingen"
            name="departments"
            rules={{ required: true }}
            options={departmentOptions}
            getOptionKey={(option) => option.id}
            getOptionLabel={getOptionLabel}
            onChange={onSelectDepartment}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            data-testid="department-multiselect"
          />
        </Grid>
        <FormRadioGroup
          control={control}
          label="Wat wil je doen met de toekomstige aanmeldingen en claims van deze flexwerker?"
          name="cancellationMode"
          onChange={(value) => {
            setValue('shouldCancelWithoutRule', value === 'cancelWithoutRule');
            setValue('shouldCancelAll', value === 'cancelAll');
          }}
          items={
            selectedFlexWorker.employmentType === EmploymentType.EMPLOYEE ||
            selectedFlexWorker.employmentType === EmploymentType.TEMPWORKER
              ? [cancelAllOption]
              : [cancelWithoutRuleOption, cancelAllOption]
          }
        />
      </Grid>
    </>
  );
};

export { NotWelcomeListAddFlexWorker };
